import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import useStyles from "./style";
import MplusApi from "Services/Mplus";
import { showSnackbar } from "Redux/SnackBar/actions";
import VehicleActionDialog from "./VehicleActionDialog";
import VehicleDetailsDialog from "./VehicleDetailsDialog";
import ProductCatalogDialog from "./ProductCatalogDialog";
import { productFeatures, useHasRole } from "Pages/ProgrammeMPlus/mplusUtils";
import {
  ACTIONS_BY_STATUS,
  VEHICLE_ACTIONS,
  VEHICLE_STATUS,
  formatMoney,
  getErrorMessage,
  locationName,
  missingLabel,
  toImageUrl,
} from "./constants";

// Somme les compteurs de la synthèse { "<modèle>": { "AVAILABLE": 2, … } }.
const sumSummary = (summary = {}, statuses) =>
  Object.values(summary).reduce(
    (total, counts) => total + statuses.reduce((sum, status) => sum + (Number(counts?.[status]) || 0), 0),
    0
  );

const EMPTY_FORM = {
  product_catalog_id: "",
  vin: "",
  plate: "",
  location_id: "",
  supplier_name: "",
  purchase_date: "",
  purchase_price: "",
  freight_cost: "",
  duty_cost: "",
  clearing_cost: "",
};

const NUMBER_FIELDS = ["purchase_price", "freight_cost", "duty_cost", "clearing_cost"];

const EmptyRowsOverlay = () => (
  <Typography className="stock-empty-state">
    Aucun véhicule ne correspond à ces filtres.
  </Typography>
);

const Stock = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [models, setModels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [menu, setMenu] = useState({ anchor: null, vehicle: null });
  const [pendingAction, setPendingAction] = useState({ action: null, vehicle: null });
  const [detailsId, setDetailsId] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const hasRole = useHasRole();

  const notify = useCallback((message, variant) => {
    dispatch(showSnackbar({
      message,
      variant,
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    }));
  }, [dispatch]);

  const loadModels = useCallback(async () => {
    try {
      const response = await MplusApi.getProductCatalog();
      setModels(response?.data?.data?.data || []);
    } catch (error) {
      setModels([]);
      notify(getErrorMessage(error, "Impossible de charger les modèles."), "error");
    }
  }, [notify]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await MplusApi.getLocations();
      const data = response?.data?.data;
      setLocations((Array.isArray(data) ? data : data?.data || []).filter((location) => location.is_active !== false));
    } catch (error) {
      setLocations([]);
    }
  }, []);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await MplusApi.getStock({
        status: statusFilter || undefined,
        product_catalog_id: modelFilter || undefined,
        location_id: locationFilter || undefined,
        page: page + 1,
        limit: pageSize,
      });
      const data = response?.data?.data || {};
      setVehicles(data.data || []);
      setTotalRecords(data.totalRecords || 0);
    } catch (error) {
      setVehicles([]);
      setTotalRecords(0);
      notify(getErrorMessage(error, "Impossible de charger le stock."), "error");
    } finally {
      setLoading(false);
    }
  }, [locationFilter, modelFilter, notify, page, pageSize, statusFilter]);

  // Les compteurs portent sur tout l'inventaire, indépendamment des filtres du tableau.
  const loadSummary = useCallback(async () => {
    try {
      const response = await MplusApi.getStock({ page: 1, limit: 1 });
      setSummary(response?.data?.data?.summary || {});
    } catch (error) {
      setSummary({});
    }
  }, []);

  useEffect(() => {
    loadModels();
    loadLocations();
    loadSummary();
  }, [loadLocations, loadModels, loadSummary]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const reload = useCallback(() => {
    loadVehicles();
    loadSummary();
  }, [loadSummary, loadVehicles]);

  // Lieux physiques où l'on peut recevoir ou envoyer un véhicule (pas « en circulation »).
  const physicalLocations = useMemo(() => locations.filter((location) => !location.is_virtual), [locations]);

  const openMenu = useCallback((event, vehicle) => setMenu({ anchor: event.currentTarget, vehicle }), []);
  const closeMenu = () => setMenu({ anchor: null, vehicle: null });

  const handleMenuAction = (action) => {
    const { vehicle } = menu;
    closeMenu();
    if (action === "details") setDetailsId(vehicle.id);
    else setPendingAction({ action, vehicle });
  };

  const modelsById = useMemo(
    () => models.reduce((result, model) => ({ ...result, [model.id]: model }), {}),
    [models]
  );

  const counts = useMemo(() => ({
    available: sumSummary(summary, ["AVAILABLE"]),
    reserved: sumSummary(summary, ["RESERVED", "CONTRACTED"]),
    active: sumSummary(summary, ["ACTIVE"]),
  }), [summary]);

  const columns = useMemo(() => [
    {
      field: "model",
      headerName: "Modèle",
      flex: 1.5,
      minWidth: 240,
      sortable: false,
      cellClassName: classes.modelCell,
      renderCell: (params) => {
        const model = params.row.product_catalog || modelsById[params.row.product_catalog_id] || {};
        const image = toImageUrl(model.image_url);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            {image ? (
              <Box component="img" src={image} alt="" sx={{ width: 52, height: 52, objectFit: "contain", borderRadius: 1, flexShrink: 0 }} />
            ) : (
              <DirectionsCarRoundedIcon sx={{ width: 52, fontSize: 36, color: "#9aa5b1", flexShrink: 0 }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }} noWrap>{model.name || "-"}</Typography>
              <Typography sx={{ fontSize: 13, color: "#687484" }} noWrap>{[model.category, ...productFeatures(model)].filter(Boolean).join(" · ")}</Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "vin",
      headerName: "N° de châssis",
      flex: 1.1,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => <span className={classes.vinCell}>{params.row.vin || "-"}</span>,
    },
    { field: "plate", headerName: "Immatriculation", flex: 0.9, minWidth: 130, sortable: false, valueGetter: (params) => params.row.plate || "-" },
    {
      field: "location",
      headerName: "Emplacement",
      flex: 1,
      minWidth: 140,
      sortable: false,
      valueGetter: (params) => locationName(params.row.location),
    },
    {
      field: "readiness",
      headerName: "Contrôles",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const missing = params.row.readiness?.missing || [];
        if (!missing.length) return <span className={classes.readyText}>Complets</span>;
        return (
          <Tooltip title={missing.map(missingLabel).join(", ")}>
            <span className={classes.missingText}>{missing.length} manquant{missing.length > 1 ? "s" : ""}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "landed_cost",
      headerName: "Coût de revient",
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      valueGetter: (params) => params.row.costs?.landed_cost,
      renderCell: (params) => (params.value == null ? "-" : formatMoney(params.value)),
    },
    {
      field: "status",
      headerName: "Statut",
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const status = VEHICLE_STATUS[params.value] || { label: params.value || "-", badge: "statusIndisponible" };
        return <span className={`${classes.statusBadge} ${classes[status.badge]}`}>{status.label}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton aria-label="Actions du véhicule" onClick={(event) => openMenu(event, params.row)}>
          <MoreVertRoundedIcon />
        </IconButton>
      ),
    },
  ], [classes, modelsById, openMenu]);

  const setField = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const vin = form.vin.trim().toUpperCase();
    const plate = form.plate.trim().toUpperCase();
    if (!form.product_catalog_id) return;
    if (!vin && !plate) {
      notify("Renseignez le n° de châssis ou l'immatriculation.", "error");
      return;
    }

    const payload = { product_catalog_id: form.product_catalog_id };
    if (vin) payload.vin = vin;
    if (plate) payload.plate = plate;
    if (form.location_id) payload.location_id = form.location_id;
    if (form.supplier_name.trim()) payload.supplier_name = form.supplier_name.trim();
    if (form.purchase_date) payload.purchase_date = form.purchase_date;
    NUMBER_FIELDS.forEach((field) => {
      if (form[field] !== "") payload[field] = Number(form[field]);
    });

    setSaving(true);
    try {
      await MplusApi.receiveVehicle(payload);
      notify("Véhicule réceptionné à l'entrepôt.", "success");
      setForm(EMPTY_FORM);
      setEntryOpen(false);
      setPage(0);
      reload();
    } catch (error) {
      notify(getErrorMessage(error, "Impossible d'enregistrer le véhicule."), "error");
    } finally {
      setSaving(false);
    }
  };

  const renderEntryFields = () => (
    <>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.fieldLabel}>Modèle <span>*</span></Typography>
        <TextField select required fullWidth size="small" value={form.product_catalog_id} SelectProps={{ displayEmpty: true }} onChange={setField("product_catalog_id")}>
          <MenuItem value="" disabled>Sélectionner un modèle</MenuItem>
          {models.map((model) => <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>)}
        </TextField>
      </Box>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.fieldLabel}>N° de châssis (VIN)</Typography>
        <TextField fullWidth placeholder="Ex : LGXCE4CB0P0123456" size="small" value={form.vin} onChange={setField("vin")} />
      </Box>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.fieldLabel}>Immatriculation</Typography>
        <TextField fullWidth placeholder="Ex : LT 123 AB" size="small" value={form.plate} onChange={setField("plate")} />
      </Box>
      <Typography className={classes.fieldHint}>Châssis ou immatriculation obligatoire.</Typography>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.fieldLabel}>Emplacement de réception</Typography>
        <TextField select fullWidth size="small" value={form.location_id} SelectProps={{ displayEmpty: true }} onChange={setField("location_id")}>
          <MenuItem value="">Entrepôt central (par défaut)</MenuItem>
          {physicalLocations.map((location) => (
            <MenuItem key={location.id} value={location.id}>{locationName(location)}{location.city ? ` · ${location.city}` : ""}</MenuItem>
          ))}
        </TextField>
      </Box>
      <Box className={classes.fieldRow}>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Fournisseur</Typography>
          <TextField fullWidth size="small" value={form.supplier_name} onChange={setField("supplier_name")} />
        </Box>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Date d'achat</Typography>
          <TextField fullWidth type="date" size="small" value={form.purchase_date} onChange={setField("purchase_date")} />
        </Box>
      </Box>
      <Box className={classes.fieldRow}>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Prix d'achat (XAF)</Typography>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} size="small" value={form.purchase_price} onChange={setField("purchase_price")} />
        </Box>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Transport (XAF)</Typography>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} size="small" value={form.freight_cost} onChange={setField("freight_cost")} />
        </Box>
      </Box>
      <Box className={classes.fieldRow}>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Douane (XAF)</Typography>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} size="small" value={form.duty_cost} onChange={setField("duty_cost")} />
        </Box>
        <Box className={classes.fieldGroup}>
          <Typography className={classes.fieldLabel}>Dédouanement (XAF)</Typography>
          <TextField fullWidth type="number" inputProps={{ min: 0 }} size="small" value={form.clearing_cost} onChange={setField("clearing_cost")} />
        </Box>
      </Box>
    </>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Stock des véhicules</Typography>
          <Typography className={classes.pageSubtitle}>Gérez vos véhicules en stock et leurs réservations</Typography>
        </Box>
        <Box className={classes.headerActions}>
          {hasRole() && (
            <Button className={classes.secondaryButton} variant="outlined" onClick={() => setCatalogOpen(true)}>
              Catalogue des modèles
            </Button>
          )}
          <Button className={classes.addButton} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setEntryOpen(true)}>
            Entrer un véhicule
          </Button>
        </Box>
      </Box>

      <Box className={classes.statsGrid}>
        <Box className={`${classes.statCard} ${classes.availableCard}`}>
          <span className={`${classes.statIcon} ${classes.availableIcon}`}><DirectionsCarRoundedIcon /></span>
          <Box><Typography className={classes.statValue}>{counts.available}</Typography><Typography className={classes.statLabel}>Disponibles</Typography></Box>
        </Box>
        <Box className={`${classes.statCard} ${classes.reservedCard}`}>
          <span className={`${classes.statIcon} ${classes.reservedIcon}`}><EventAvailableRoundedIcon /></span>
          <Box><Typography className={classes.statValue}>{counts.reserved}</Typography><Typography className={classes.statLabel}>Réservés / sous contrat</Typography></Box>
        </Box>
        <Box className={`${classes.statCard} ${classes.deliveredCard}`}>
          <span className={`${classes.statIcon} ${classes.deliveredIcon}`}><LocalShippingRoundedIcon /></span>
          <Box><Typography className={classes.statValue}>{counts.active}</Typography><Typography className={classes.statLabel}>En circulation</Typography></Box>
        </Box>
      </Box>

      <Box className={classes.contentGrid}>
        <Box className={classes.listPanel}>
          <Box className={classes.panelHeader}>
            <Typography className={classes.panelTitle}>Inventaire des véhicules</Typography>
            <Box className={classes.listTools}>
              <TextField
                select
                size="small"
                className={classes.filterField}
                value={statusFilter}
                SelectProps={{ displayEmpty: true }}
                onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">Tous les statuts</MenuItem>
                {Object.entries(VEHICLE_STATUS).map(([value, status]) => <MenuItem key={value} value={value}>{status.label}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                className={classes.filterField}
                value={modelFilter}
                SelectProps={{ displayEmpty: true }}
                onChange={(event) => { setModelFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">Tous les modèles</MenuItem>
                {models.map((model) => <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                className={classes.filterField}
                value={locationFilter}
                SelectProps={{ displayEmpty: true }}
                onChange={(event) => { setLocationFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value="">Tous les emplacements</MenuItem>
                {locations.map((location) => <MenuItem key={location.id} value={location.id}>{locationName(location)}</MenuItem>)}
              </TextField>
              <IconButton className={classes.refreshButton} aria-label="Actualiser le stock" disabled={loading} onClick={reload}>
                <RefreshRoundedIcon />
              </IconButton>
            </Box>
          </Box>
          <Box className={classes.tableWrapper}>
            <DataGrid
              className={classes.dataGridTable}
              rows={vehicles}
              loading={loading}
              columns={columns}
              page={page}
              pageSize={pageSize}
              rowCount={totalRecords}
              paginationMode="server"
              onPageChange={setPage}
              onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              pagination
              autoHeight
              disableSelectionOnClick
              scrollbarSize={4}
              rowHeight={72}
              headerHeight={48}
              components={{ NoRowsOverlay: EmptyRowsOverlay }}
              sx={{ width: "100%" }}
            />
          </Box>
        </Box>

        <Box className={classes.entryPanel}>
          <Box className={classes.entryHeading}>
            <Typography className={classes.panelTitle}>Entrée en stock</Typography>
            <Typography className={classes.entryDescription}>Réceptionnez un véhicule acheté à l'entrepôt central.</Typography>
          </Box>
          <Box component="form" className={classes.entryForm} onSubmit={handleSubmit}>
            {renderEntryFields()}
            <Button className={classes.saveButton} type="submit" variant="contained" disabled={saving} startIcon={<SaveOutlinedIcon />}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog open={entryOpen} onClose={() => setEntryOpen(false)} fullWidth maxWidth="sm" className={classes.entryDialog}>
        <DialogTitle className={classes.entryDialogTitle}>
          <Box>
            <Typography className={classes.dialogTitleText}>Entrée en stock</Typography>
            <Typography className={classes.dialogSubtitle}>Réceptionner un véhicule acheté</Typography>
          </Box>
          <IconButton aria-label="Fermer" onClick={() => setEntryOpen(false)} size="small"><CloseRoundedIcon /></IconButton>
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent className={classes.entryDialogContent}>
            <Box className={classes.entryForm}>{renderEntryFields()}</Box>
          </DialogContent>
          <DialogActions className={classes.entryDialogActions}>
            <Button className={classes.cancelButton} onClick={() => setEntryOpen(false)}>Annuler</Button>
            <Button className={classes.saveButton} type="submit" variant="contained" disabled={saving} startIcon={<SaveOutlinedIcon />}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
        <MenuItem onClick={() => handleMenuAction("details")}>
          <ListItemText primary="Voir la fiche" secondary="Informations, contrôles, historique" />
        </MenuItem>
        {(ACTIONS_BY_STATUS[menu.vehicle?.status] || []).map((action) => (
          <MenuItem key={action} onClick={() => handleMenuAction(action)} className={VEHICLE_ACTIONS[action].danger ? classes.dangerMenuItem : undefined}>
            {VEHICLE_ACTIONS[action].label}
          </MenuItem>
        ))}
      </Menu>

      <VehicleActionDialog
        action={pendingAction.action}
        vehicle={pendingAction.vehicle}
        locations={physicalLocations}
        notify={notify}
        onClose={() => setPendingAction({ action: null, vehicle: null })}
        onDone={() => { setPendingAction({ action: null, vehicle: null }); reload(); }}
      />

      <ProductCatalogDialog open={catalogOpen} notify={notify} onClose={() => setCatalogOpen(false)} onChanged={loadModels} />

      <VehicleDetailsDialog
        vehicleId={detailsId}
        notify={notify}
        onClose={() => setDetailsId(null)}
        onChanged={reload}
      />
    </Box>
  );
};

export default Stock;
